/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { shallowEqual, useSelector } from "react-redux"

import { ECOMMERCE_MAIN_MERCHANT_ID_KEY } from "ecommerce/constants"
import { useIsMerchantLevelUser } from "ecommerce/hooks/useIsMerchantLevelUser"
import {
  FacetTypes,
  GrabXDataTypes,
  MerchantPortalFeatureFlag,
  useGrabXFeatureFlag,
} from "grabx"
import { AppState } from "store/store.type"
import { getUserStoreID, hasEcommerceStore } from "utils/user_helper"

interface UseGetEcommerceMerchantIDResult {
  merchantID: string
  isEcomMultiLocationEnabled: boolean
  isMerchantLevelUser: boolean
  isLoading: boolean
}

export const useGetEcommerceMerchantID = (): UseGetEcommerceMerchantIDResult => {
  const { outlets, merchantGroupID } = useSelector(
    (state: AppState) => ({
      outlets: state.menu.outlets,
      merchantGroupID: state.menu.merchantGroupID,
    }),
    shallowEqual,
  )
  const isMerchantLevelUser = useIsMerchantLevelUser()

  const isEcom = hasEcommerceStore()
  const initialMerchantID =
    (isMerchantLevelUser && outlets?.[0]?.merchantID) || getUserStoreID() || ""
  const isSingleStore = (outlets?.length ?? 0) <= 1

  const { data: isEcomMultiLocationEnabled, isFetching } = useGrabXFeatureFlag(
    MerchantPortalFeatureFlag.enableEcomMenuGroupProductCreation,
    GrabXDataTypes.Boolean,
    false,
    {
      [FacetTypes.Merchant]: initialMerchantID,
      [FacetTypes.MexMerchant]: merchantGroupID,
    },
    isEcom && !!initialMerchantID,
  )

  let merchantID = ""
  if (isEcom) {
    merchantID =
      isEcomMultiLocationEnabled && isMerchantLevelUser && !isSingleStore
        ? ECOMMERCE_MAIN_MERCHANT_ID_KEY
        : initialMerchantID
  }

  return {
    merchantID,
    isEcomMultiLocationEnabled: !!isEcomMultiLocationEnabled,
    isMerchantLevelUser,
    isLoading: isFetching,
  }
}
